/**
 * The draft's explicit-save count as THIS device last saw it (offline plan T4c, #5993 review).
 *
 * The server counts the owner's explicit saves of a draft (Save Draft / Submit, any device:
 * `explicitSaveCount`). A background save carries the count its device last saw as
 * `explicitSaveBase`; when the server's count is higher, the owner saved on purpose after the copy
 * was made and the copy never saw it, so the later save stands and the copy's differing values are
 * kept aside (never applied, never dropped).
 *
 * This ledger is how the device knows its count. It is fed by:
 *  - every explicit save's answer (the forms callable wrappers, useFormSubmissions), so the device
 *    that saved is never mistaken for a stale one;
 *  - the draft as loaded on resume (FormFiller), since what is on screen came from it.
 * Kept in localStorage so a reload does not reset it to 0 (that would make every later background
 * save of this device look stale). Only ever moves up. Wiped with the rest of a technician's local
 * form state on a user switch (useFormDrafts.clearLocalFormDrafts).
 */
export const EXPLICIT_SAVE_PREFIX = 'form-explicit-saves:';

const keyFor = (submissionId: string): string => `${EXPLICIT_SAVE_PREFIX}${submissionId}`;

/** The count this device last saw for the draft; 0 when it has seen none. */
export function explicitSaveBaseFor(submissionId: string): number {
  try {
    const raw = localStorage.getItem(keyFor(submissionId));
    const n = raw === null ? 0 : Number(raw);
    return Number.isSafeInteger(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Record a count the device has now seen (an explicit save's answer, or the loaded draft). */
export function noteExplicitSaveCount(submissionId: string, count: unknown): void {
  if (typeof count !== 'number' || !Number.isSafeInteger(count) || count <= 0) return;
  if (count <= explicitSaveBaseFor(submissionId)) return;
  try {
    localStorage.setItem(keyFor(submissionId), String(count));
  } catch {
    // Storage unavailable: the in-flight value is lost, so later background saves of this device
    // may be kept aside rather than applied. Safe (nothing is overwritten or dropped), only noisier.
  }
}

/** Forget a draft that was submitted or deleted. */
export function forgetExplicitSaveCount(submissionId: string): void {
  try {
    localStorage.removeItem(keyFor(submissionId));
  } catch {
    // Nothing stored.
  }
}
